import { ReactNode, useEffect } from 'react'
import { motion } from 'framer-motion'
import { cn } from '$components'
import { ModalContextProvider } from './context'
import { transition } from './animation'
import { ModalHeader } from './header'
import { ModalContent } from './content'
import { ModalHeading } from './heading'
import { ModalFooter } from './footer'
import { useModalRegistry } from '../../contexts/modal-registry'
import { useKeybind } from '../../hooks'

export type ModalProps = {
  children?: ReactNode,
  className?: string,
  onClose: () => void,
}

export function Modal({ children, className, onClose }: ModalProps) {
  const { openModal, closeModal } = useModalRegistry()

  useEffect(() => {
    openModal()
    return () => closeModal()
  }, [openModal, closeModal])

  useKeybind({ key: 'Escape', onKeyPress: onClose })

  return (
    <ModalContextProvider onClose={onClose}>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed top-0 left-0 bg-black/10 w-full h-full cursor-default z-[60]"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        initial="initial"
        animate="in"
        exit="out"
        variants={transition}
        className={cn(
          'fixed top-0 left-0 w-full h-full flex justify-center z-[70] pointer-events-none',
          'items-end [@media(min-width:640px)]:items-center',
        )}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'bg-white flex flex-col max-h-[90%] max-w-[640px] w-full pointer-events-auto',
            '[@media(min-width:640px)]:rounded-b-3xl rounded-t-3xl',
            className,
          )}
        >
          {children}
        </div>
      </motion.div>
    </ModalContextProvider>
  )
}

Modal.Header = ModalHeader
Modal.Content = ModalContent
Modal.Heading = ModalHeading
Modal.Footer = ModalFooter
